import {
    Box,
    Button,
    Container,
    Paper,
    CircularProgress,
    Alert,
    useTheme,
    alpha
} from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useEffect, useState } from 'react';

import Footer from '../components/Footer';
import axiosInstance from '../services/axiosInstance';
import Header from '../components/Header';

import { decodeId } from '../utils/idEncoder';

// ==============================
// TYPE DEFINITIONS
// ==============================

interface ArticleDetailData {
    articleId: number;
    contentDetails: string;
}

const ArticlePage = () => {
    const navigate = useNavigate();
    const { id: hashId } = useParams<{ id: string }>();
    const realVehicleId = hashId ? decodeId(hashId) : null;
    const theme = useTheme();

    const [article, setArticle] = useState<ArticleDetailData | null>(null);
    const [loadingArticle, setLoadingArticle] = useState(false);
    const [error, setError] = useState<string | null>(null);

    useEffect(() => {
        const fetchArticle = async () => {
            if (!realVehicleId) return;

            setLoadingArticle(true);
            setError(null);
            try {
                const response = await axiosInstance.get(
                    `/CustomerVehicle/ArticleSingleData/${realVehicleId}`
                );

                // Use 'any' casting temporarily if strict types aren't defined for the full response wrapper
                const result = response.data as any;

                if (result.responseCode === "00" && result.responseData) {
                    setArticle(result.responseData);
                } else {
                    setError(result.responseMessage || "Failed to load article.");
                }
            } catch (err) {
                console.error("API Error:", err);
                setError("An error occurred while fetching the article.");
            } finally {
                setLoadingArticle(false);
            }
        };

        fetchArticle();
    }, [realVehicleId]);

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: '100vh' }}>
            {/* Header */}
            <Header />

            {/* Main Content Wrapper - Light Gray Background to pop the paper */}
            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    bgcolor: '#f4f6f8', // Light gray background
                    py: 6, // Vertical padding (removes the need for negative margins)
                }}
            >
                <Container maxWidth="md">

                    {/* Navigation / Back Button */}
                    <Box sx={{ mb: 3 }}>
                        <Button
                            startIcon={<ArrowBack />}
                            onClick={() => navigate(-1)}
                            sx={{
                                color: 'text.secondary',
                                fontWeight: 600,
                                '&:hover': { bgcolor: 'transparent', color: 'primary.main' }
                            }}
                        >
                            Back to Articles
                        </Button>
                    </Box>

                    {/* Loading State */}
                    {loadingArticle && (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 10 }}>
                            <CircularProgress />
                        </Box>
                    )}

                    {/* Error State */}
                    {!loadingArticle && error && (
                        <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>
                    )}

                    {/* Article Content */}
                    {!loadingArticle && article && (
                        <Paper
                            elevation={0} // Flat style is more modern, or use elevation={1}
                            sx={{
                                p: { xs: 3, md: 6 }, // More padding on desktop
                                borderRadius: 3,
                                border: '1px solid',
                                borderColor: 'divider',
                                bgcolor: 'background.paper',
                                overflow: 'hidden' // Prevents large images from breaking layout
                            }}
                        >
                            <Box
                                sx={{
                                    // GLOBAL HTML STYLES
                                    // These target the tags inside dangerouslySetInnerHTML
                                    '& h1, & h2, & h3': {
                                        color: 'text.primary',
                                        fontWeight: 800,
                                        lineHeight: 1.3,
                                        mb: 2,
                                        mt: 4
                                    },
                                    '& h1': { fontSize: { xs: '2rem', md: '2.5rem' }, mt: 0 }, // No top margin for first title
                                    '& h2': { fontSize: { xs: '1.5rem', md: '2rem' } },
                                    '& h3': { fontSize: { xs: '1.25rem', md: '1.75rem' } },

                                    '& p': {
                                        fontSize: '1.05rem',
                                        lineHeight: 1.8, // Better readability
                                        color: 'text.secondary',
                                        mb: 3,
                                    },
                                    '& strong': {
                                        fontWeight: 700,
                                        color: 'text.primary'
                                    },
                                    '& ul, & ol': {
                                        mb: 3,
                                        pl: 4,
                                        color: 'text.secondary'
                                    },
                                    '& li': {
                                        mb: 1,
                                        lineHeight: 1.6
                                    },
                                    // Image Styling
                                    '& img': {
                                        display: 'block',
                                        maxWidth: '100%',
                                        height: 'auto',
                                        borderRadius: 2,
                                        my: 4,
                                        boxShadow: `0 4px 12px ${alpha(theme.palette.common.black, 0.1)}`
                                    },
                                    // Blockquote styling
                                    '& blockquote': {
                                        borderLeft: `4px solid ${theme.palette.primary.main}`,
                                        m: 0,
                                        my: 4,
                                        pl: 3,
                                        py: 1,
                                        bgcolor: alpha(theme.palette.primary.main, 0.05),
                                        fontStyle: 'italic',
                                        color: 'text.primary'
                                    }
                                }}
                                dangerouslySetInnerHTML={{ __html: article.contentDetails }}
                            />
                        </Paper>
                    )}
                </Container>
            </Box>

            <Footer />
        </Box>
    );
};

export default ArticlePage;